import React from 'react';
import { Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { ServicesContext } from '~/services';
import { enumValue, enums, formatDateTime, handleButtonKeypress } from '~/util';
import { OutgoingReferralType } from '../OutgoingReferrals';
import { OpenLR } from '../../OpenLR';
import { ReferralStatusBadge } from '../ReferralStatusBadge';

interface OutgoingReferralModalProps {
    outgoingReferral: OutgoingReferralType;
    children: React.ReactNode;
}
export function OutgoingReferralModal({ outgoingReferral, children }: OutgoingReferralModalProps) {
    const services = React.useContext(ServicesContext);
    const [show, setShow] = React.useState(false);

    async function openLrLink(transmissions = false) {
        const result = await services.api.call({
            method: 'post',
            url: '/api/access-tokens',
            data: { type: enumValue(['user', 'tokens', 'Core App Auth Token']) },
        });
        window.open(
            `${result.data.host}/referral/${outgoingReferral.message.referral_message.base36Id}${
                transmissions ? '/transmissions' : ''
            }?f=out&zephyr_access_token=${result.data.token}`,
            '_blank'
        );
    }

    function handleShow() {
        setShow(true);
    }

    function handleClose() {
        setShow(false);
    }

    return (
        <>
            <div
                onClick={handleShow}
                onKeyUp={(e) => handleButtonKeypress(e, handleShow)}
                role="button"
                tabIndex={0}
            >
                {children}
            </div>
            <BSModal
                show={show}
                onHide={handleClose}
                className="outgoing-referral-modal"
                aria-labelledby={`outgoing-referral-${outgoingReferral.id}`}
            >
                <Modal.Header closeButton>
                    <Modal.Title
                        id={`outgoing-referral-${outgoingReferral.id}`}
                        role="heading"
                        aria-level={1}
                    >
                        Referral Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {outgoingReferral.message.unread_notes_count > 0 && (
                        <div className="permissions-alert permissions-alert-card">
                            You have{' '}
                            <button
                                className="link-button"
                                type="button"
                                onClick={() => openLrLink()}
                            >
                                {`${outgoingReferral.message.unread_notes_count} unread message${
                                    outgoingReferral.message.unread_notes_count > 1 ? 's' : ''
                                }`}
                            </button>{' '}
                            in LeadingReach.
                        </div>
                    )}
                    {outgoingReferral.message.failed_transmissions_count > 0 && (
                        <div
                            className={`permissions-alert permissions-alert-card ${
                                outgoingReferral.message.unread_notes_count > 0 ? 'border-top' : ''
                            }`}
                        >
                            <button
                                className="link-button"
                                type="button"
                                onClick={() => openLrLink(true)}
                            >
                                {`${outgoingReferral.message.failed_transmissions_count} ${
                                    outgoingReferral.message.failed_transmissions_count > 1
                                        ? 'communications'
                                        : 'communication'
                                } failed`}
                            </button>{' '}
                        </div>
                    )}
                    <div className="outgoing-referral-details">
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Status:</div>
                            <div className="col-8">
                                <ReferralStatusBadge referral={outgoingReferral.message} />
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Referral ID:</div>
                            <div className="col-8">
                                {outgoingReferral.message.referral_message.base36Id}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Created:</div>
                            <div className="col-8">
                                {formatDateTime(outgoingReferral.message.created)}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Updated:</div>
                            <div className="col-8">
                                {formatDateTime(outgoingReferral.message.updated)}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Type:</div>
                            <div className="col-8">
                                {enums([
                                    'core_app',
                                    'referral_types',
                                    outgoingReferral.message.referral_message.type,
                                ])}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">To:</div>
                            <div className="col-8">
                                {outgoingReferral.message.referral_message.to_name
                                    ? `${outgoingReferral.message.referral_message.to_name} at `
                                    : ''}
                                {outgoingReferral.message.referral_message.to_organization_name}
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-4 fw-bold">Location:</div>
                            <div className="col-8">
                                {outgoingReferral.message.referral_message.location?.name || '--'}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-4 fw-bold">Referral Reason:</div>
                            <div className="col-8">
                                {outgoingReferral.message.referral_message.reason}
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <OpenLR referralID={outgoingReferral.message.referral_message.base36Id} />
                    <button className="btn btn-secondary" type="button" onClick={handleClose}>
                        Close
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}
